import type { KollaProvidersEntityOrResourcesEntity } from 'app/services/kolla'
import { LightBulbOn, RefreshDouble } from 'iconoir-react'
import ProviderPanel from 'app/components/scheduling/ProviderPanel'
import { Tooltip } from 'app/components/Tooltip'
import { FilterBy } from 'app/components/viz/FilterBy'
import { AppointmentTypeJson } from 'app/types/entities'
import { ProviderType } from 'app/types/entities'
import type { UseAiAgentAvailabilityReturn } from './useAiAgentAvailability'

export type AiAgentAvailabilityComponentProps = UseAiAgentAvailabilityReturn

export function AiAgentAvailabilityComponent(props: AiAgentAvailabilityComponentProps) {
  const {
    user,
    bookableAppointmentTypes,
    operatories,
    isSyncing,
    isSyncingSchedules,
    rows,
    view,
    activeProviders,
    inactiveProviders,
    isDentrix,
    onRefreshClick,
    onSyncSchedulesClick,
    onViewChange,
    handleProviderStatusChange,
  } = props

  return (
    <div className="my-4 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <p className="text-ui-sm text-strong">Providers</p>
          <p className="rounded-full bg-blue-200 px-2 py-1 text-caption-sm !font-medium text-subtle">
            {activeProviders.length} Active
          </p>
          <p className="rounded-full bg-surface-strong px-2 py-1 text-caption-sm !font-medium text-subtle">
            {inactiveProviders.length} Inactive
          </p>
        </div>
        <div className="flex items-center gap-4">
          {user?.type === `super` && (
            <button className="btn btn-sm btn-tertiary" disabled={isSyncing} onClick={() => onSyncSchedulesClick()}>
              <RefreshDouble className={isSyncingSchedules ? `animate-spin` : ``} /> <p>Sync PMS Schedules</p>
            </button>
          )}
          <Tooltip
            message={
              <div className="flex max-w-xs flex-col gap-2 p-1">
                <div className="flex items-center gap-2">
                  <LightBulbOn height={16} width={16} strokeWidth={2} className="text-ui-sm inverse-text-strong" />
                  <p className="text-ui-sm inverse-text-strong">Adding New Providers</p>
                </div>
                <p className="whitespace-pre-wrap text-body-xs inverse-text-default">
                  Providers are added as inactive by default. When new providers are added to this list please make sure
                  you are viewing "all" via the dropdown to the right.
                </p>
              </div>
            }
            // className={currentValues ? `cursor-not-allowed` : ``}
          >
            <button className="btn btn-sm btn-tertiary" disabled={isSyncing} onClick={() => onRefreshClick()}>
              <RefreshDouble className={isSyncing ? `animate-spin` : ``} /> <p>Sync Providers</p>
            </button>
          </Tooltip>
          <FilterBy
            defaultFilter={view}
            value={view}
            options={[
              { value: `all`, display: `all` },
              { value: `active`, display: `active` },
              { value: `inactive`, display: `inactive` },
            ]}
            onChange={(option) => onViewChange(option.value as `all` | `active` | `inactive`)}
          />
        </div>
      </div>
      {view === `all` &&
        rows.map((provider) => (
          <ProviderPanel
            key={provider.id}
            provider={provider}
            appointmentTypes={bookableAppointmentTypes?.hits as AppointmentTypeJson[]}
            operatories={operatories as KollaProvidersEntityOrResourcesEntity[]}
            onStatusChange={(status) => handleProviderStatusChange(provider, status)}
            isSecondary={isDentrix && !provider.isPrimary}
          />
        ))}
      {view === `active` &&
        activeProviders.map((provider) => (
          <ProviderPanel
            key={provider.id}
            provider={provider}
            appointmentTypes={bookableAppointmentTypes?.hits as AppointmentTypeJson[]}
            operatories={operatories as KollaProvidersEntityOrResourcesEntity[]}
            onStatusChange={(status) => handleProviderStatusChange(provider, status)}
            isSecondary={isDentrix && !provider.isPrimary}
          />
        ))}
      {view === `inactive` &&
        inactiveProviders.map((provider) => (
          <ProviderPanel
            key={provider.id}
            provider={provider}
            appointmentTypes={bookableAppointmentTypes?.hits as AppointmentTypeJson[]}
            operatories={operatories as KollaProvidersEntityOrResourcesEntity[]}
            onStatusChange={(status) => handleProviderStatusChange(provider, status)}
            isSecondary={isDentrix && !provider.isPrimary}
          />
        ))}
    </div>
  )
}
